import type { ReactNode } from "react";
import type { AppError } from "@/api/runtime";
import { isClientRefusal } from "@/api/useTool";
import { useRoute } from "@/router";
import { NotFound } from "@/views/NotFound";
import { Banner, Skeleton } from "@/ui";

import { ArchiveSnapshot } from "@/views/AdminArchive";
import { V1Slot } from "@/shell/island";

/** One boundary for record reads. Refused content (including stale data) never mounts. Its own screens are V1, so in a
 * V2 view's island they render through the V1 slot (shell/island.tsx); the children stay where they are. */
export function ObjectReadGuard({ query, children, allowed = true }: {
  query: { data: unknown; error: AppError | null; refetch: () => unknown };
  children?: ReactNode;
  allowed?: boolean;
}) {
  const route = useRoute();
  if (query.error?.code === "RECORD_ARCHIVED" && route.view === "requisitions" && /^R-\d+$/.test(route.parts[0] ?? "")) return <V1Slot><ArchiveSnapshot id={route.parts[0]!} /></V1Slot>;
  if (isClientRefusal(query.error)) return <NotFound route={route.raw} />;
  if (query.error) return <V1Slot><Banner error={query.error} onRetry={() => { void query.refetch(); }} /></V1Slot>;
  if (!query.data) return <V1Slot><Skeleton /></V1Slot>;
  if (!allowed) return <NotFound route={route.raw} />;
  return <>{children}</>;
}
